import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Receipt, Wallet, CalendarClock } from "lucide-react";

const STATUS_STYLE = {
  SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  CANCELLED: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [txns, setTxns] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = () => {
    api.get("/subscriptions/mine").then((r) => setSub(r.data)).catch(() => {});
    api.get("/subscriptions/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/transactions/mine").then((r) => setTxns(r.data)).catch(() => {});
    api.get("/invoices/mine").then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const download = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const expiration = sub?.expiration_date ? new Date(sub.expiration_date) : null;
  const daysLeft = expiration ? Math.max(0, Math.floor((expiration - new Date()) / 86400000)) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Mon abonnement & paiements</h1>
        <p className="text-sm text-slate-500">Facturation SaaS Transport.pro</p>
      </div>

      {/* Current subscription card */}
      <Card data-testid="my-sub-card">
        <CardHeader className="pb-3">
          <div className="text-xs uppercase tracking-widest text-orange-500 font-semibold">Abonnement en cours</div>
          <CardTitle className="font-display flex items-center gap-2"><Wallet className="w-5 h-5 text-orange-500" /> {sub?.plan_name || "FREE"}</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Statut</div>
            <div className="mt-1"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${sub?.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>{sub?.status || "—"}</span></div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Cycle</div>
            <div className="mt-1 font-semibold">{sub?.billing_cycle || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Expiration</div>
            <div className="mt-1 font-semibold">
              {expiration ? expiration.toLocaleDateString("fr-FR") : "—"}
              {daysLeft !== null && <span className="text-xs text-slate-500 ml-2">({daysLeft} j restants)</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display flex items-center gap-2"><Receipt className="w-5 h-5 text-orange-500" /> Factures ({invoices.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">N° facture</th>
                <th className="text-left">Plan</th>
                <th className="text-left">Cycle</th>
                <th className="text-right">Montant</th>
                <th className="text-left">Date</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                  <td className="p-3 font-mono">{inv.number}</td>
                  <td className="font-semibold">{inv.plan_name}</td>
                  <td className="text-xs">{inv.billing_cycle}</td>
                  <td className="text-right font-mono">{formatCurrency(inv.amount, inv.currency)}</td>
                  <td className="text-xs">{new Date(inv.issued_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => download(inv)} data-testid={`invoice-dl-${inv.id}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aucune facture (les factures sont générées après confirmation de paiement)</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Payment history */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display">Historique des paiements ({txns.length})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Référence</th>
                <th className="text-left">Plan</th>
                <th className="text-right">Montant</th>
                <th className="text-left">Fournisseur</th>
                <th className="text-left">Statut</th>
                <th className="text-left p-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {txns.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                  <td className="p-3 font-mono text-xs">{t.transaction_ref}</td>
                  <td className="font-semibold">{(t.plan_id || "").toUpperCase()} <span className="text-xs text-slate-500">{t.billing_cycle}</span></td>
                  <td className="text-right font-mono">{formatCurrency(t.amount, t.currency)}</td>
                  <td className="text-xs uppercase">{t.provider_key}</td>
                  <td><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[t.status] || STATUS_STYLE.PENDING}`}>{t.status}</span></td>
                  <td className="p-3 text-xs">{new Date(t.created_at).toLocaleString("fr-FR")}</td>
                </tr>
              ))}
              {txns.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aucun paiement effectué</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
